<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useCommonInfoService } from '@/entities/Services/CommonInfo';
import { useContactsStore } from '@/entities/Stores/Contacts';

import { B2SPopup, useIsB2SActive } from '@/features/B2S';

import { FeedbackForm } from '@/features/FeedbackForm';
import { useFlowController } from '@/features/flow/Flow';

import { useFlowLoadingEmitter, useGlobalEventEmitter } from '@/shared/composables';
import { Errors } from '@/shared/constants';
import { FailedToLoad } from '@/widgets/FailedToLoad';

import { FlowContainer } from '@/widgets/FlowContainer';

import { RenewalSkeleton } from '@/widgets/RenewalSkeleton';

const { handleOnBack, error, resolveError, loading, onLoading, handleError, isMainLoader }
  = useFlowController();
const eventEmitter = useGlobalEventEmitter();
const emitLoading = useFlowLoadingEmitter();
const { isB2SActive } = useIsB2SActive();

eventEmitter.on(Errors.NETWORK_ERROR, handleError);

const route = useRoute();
const commonInfoService = useCommonInfoService();
const contactStore = useContactsStore();

const withFeedback = computed(() => Boolean(route.meta.withFeedback));
const showFeedback = ref(withFeedback.value);

const { mutateAsync: fetchContacts } = useMutation({
  mutationFn: () => commonInfoService.fetchContacts(),
});

onMounted(async () => {
  if (contactStore.contacts.length) {
    return;
  }
  emitLoading(async () => {
    const data = await fetchContacts();
    contactStore.contacts = Object.values(data);
  });
});
</script>

<template>
  <div id="download-banner-container" />
  <B2SPopup v-if="isB2SActive && !loading && !error" />
  <FlowContainer
    class="flow-container"
    :class="{ 'flow-container--b2s': isB2SActive }"
    @back="handleOnBack"
  >
    <FailedToLoad v-if="error" :error="error" @resolve="resolveError" />
    <template v-else>
      <RenewalSkeleton v-show="loading" :is-main-loader="isMainLoader" />
      <RouterView v-show="!loading" @load="onLoading" />
    </template>
    <FeedbackForm v-if="showFeedback" v-model="showFeedback" data-test="feedback-form" />
  </FlowContainer>
</template>

<style scoped lang="scss">
@use 'RenewalLayout';
</style>
